/**
 * Massing & Site tab — Property Line, Graded Region, Split/Merge,
 * Place Mass and Curtain Panel by Face tools.
 *
 * Every tool writes real elements through bimStore.addElement so results
 * participate in undo, selection and the contextual Modify tab.
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool, ToolContext } from '../BaseTool';
import { Element, MassElement } from '../../core/types';
import {
    Polygon2D,
    polygonArea,
    splitPolygonByLine,
    convexHull,
    regularPolygon,
} from '../../utils/siteGeometry';
import i18n from '../../i18n';

// ==================== Shared helpers ====================

/** First project level name (or 'Level 1' when no levels exist). */
function firstLevelName(ctx: ToolContext): string {
    let name = 'Level 1';
    let min = Infinity;
    ctx.bimStore.project.levels.forEach((l) => {
        if (l.elevation < min) {
            min = l.elevation;
            name = l.name;
        }
    });
    return name;
}

/** Rubber-band polyline preview (also used for the closing segment). */
function pushPolylinePreview(
    ctx: ToolContext,
    points: THREE.Vector3[],
    current: THREE.Vector3 | null,
    color: string,
    close = false
): void {
    if (points.length === 0) {
        ctx.appStore.setToolPreview(null);
        return;
    }
    const pts = [...points];
    if (current) pts.push(current);
    if (close && pts.length > 2) pts.push(pts[0].clone());
    ctx.appStore.setToolPreview({ type: 'polyline', points: pts, color });
}

// ==================== Property Line ====================

/**
 * Property Line: click-click sketch of the parcel boundary; closes on first
 * point or Enter. Creates a 'Property Lines' element (dashed red boundary)
 * exactly like BIM's Site Element panel.
 */
export class PropertyLineTool extends BaseTool {
    id = 'property_line';
    name = 'Property Line';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.propertyLineClickBoundaryPointsClick'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        // Close the loop when clicking near the first point.
        if (this.points.length >= 3 && snapped.distanceTo(this.points[0]) < 0.3) {
            this.finish();
            return;
        }
        this.points.push(snapped);
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length >= 2) {
            this.finish();
        } else if (event.key === 'Escape' && this.points.length > 0) {
            this.points = [];
            this.currentPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    private updatePreview(): void {
        if (!this.context) return;
        pushPolylinePreview(this.context, this.points, this.currentPoint, '#ff4b3e');
    }

    private finish(): void {
        if (!this.context || this.points.length < 2) return;
        const pts = this.points.map((p) => [p.x, p.y] as [number, number]);
        const line: Element = {
            id: uuidv4(),
            category: 'Property Lines',
            type: 'Site Property Line',
            name: 'Property Line',
            parameters: {
                'Length': `${polygonPerimeter(pts).toFixed(2)} m`,
                'Area': `${polygonArea(pts).toFixed(2)} m²`,
                'Level': firstLevelName(this.context),
            },
            constraints: [],
            geometryData: { boundary: pts, closed: true },
        };
        this.context.bimStore.addElement(line);
        this.notify(i18n.t('tools.notify.propertyLineCreated'), 'success');
        this.points = [];
        this.currentPoint = null;
        this.context.appStore.setToolPreview(null);
    }
}

function polygonPerimeter(poly: Polygon2D): number {
    let len = 0;
    for (let i = 0; i < poly.length; i++) {
        const j = (i + 1) % poly.length;
        len += Math.hypot(poly[j][0] - poly[i][0], poly[j][1] - poly[i][1]);
    }
    return len;
}

// ==================== Graded Region ====================

/**
 * Graded Region: sketch the region, then create the "existing grading" copy
 * (dashed) of every toposolid footprint inside the region. BIM compares the
 * existing surface against the graded (proposed) one for Cut/Fill takeoff —
 * the same comparison is stored on the element parameters.
 */
export class GradedRegionTool extends BaseTool {
    id = 'graded_region';
    name = 'Graded Region';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.gradedRegionSketchTheAreaTo'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        if (this.points.length >= 3 && snapped.distanceTo(this.points[0]) < 0.3) {
            this.finish();
            return;
        }
        this.points.push(snapped);
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length >= 3) {
            this.finish();
        } else if (event.key === 'Escape' && this.points.length > 0) {
            this.points = [];
            this.currentPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    private updatePreview(): void {
        if (!this.context) return;
        pushPolylinePreview(this.context, this.points, this.currentPoint, '#e6a23c', true);
    }

    private finish(): void {
        if (!this.context || this.points.length < 3) return;
        const region: Polygon2D = this.points.map((p) => [p.x, p.y]);
        const props = this.context.bimStore.toolProperties;
        const elevation = props.gradedRegionElevation ?? 0;

        // Existing grading snapshot: reference the toposolids overlapping the
        // region (point-in-region or region-inside-boundary approximation).
        const existingIds: string[] = [];
        this.context.bimStore.project.elements.forEach((el) => {
            if (el.category !== 'Toposolid') return;
            const b = (el.geometryData as any)?.boundary as Polygon2D | undefined;
            if (!b) return;
            const overlaps = b.some((p) => pointInConvexRegion(p, region))
                || region.some((p) => pointInPolygonSimple(p, b));
            if (overlaps) existingIds.push(el.id);
        });

        const region3: Element = {
            id: uuidv4(),
            category: 'Graded Regions',
            type: 'Graded Region',
            name: 'Graded Region',
            parameters: {
                'Area': `${polygonArea(region).toFixed(2)} m²`,
                'Proposed Elevation Offset': `${elevation.toFixed(2)} m`,
                'Existing Surfaces': existingIds.length,
                'Cut/Fill Volume': `${estimateCutFill(region, elevation).toFixed(1)} m³`,
                'Referenced Toposolids': existingIds.join(', ') || 'None',
            },
            constraints: existingIds.map((id) => ({ type: 'HOST_CONTAINED' as const, targetId: id, data: {} })),
            geometryData: { boundary: region, elevationOffset: elevation },
        };
        this.context.bimStore.addElement(region3);
        this.notify(i18n.t('tools.notify.gradedRegionCreatedCutFillComputed'), 'success');
        this.points = [];
        this.currentPoint = null;
        this.context.appStore.setToolPreview(null);
    }
}

/** Crude cut/fill volume: area × signed elevation offset. */
function estimateCutFill(region: Polygon2D, elevation: number): number {
    return polygonArea(region) * Math.abs(elevation);
}

function pointInConvexRegion(p: [number, number], poly: Polygon2D): boolean {
    return pointInPolygonSimple(p, poly);
}

function pointInPolygonSimple(p: [number, number], poly: Polygon2D): boolean {
    let inside = false;
    const n = poly.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
        const [xi, yi] = poly[i];
        const [xj, yj] = poly[j];
        if (yi > p[1] !== yj > p[1] && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) {
            inside = !inside;
        }
    }
    return inside;
}

// ==================== Split Toposolid ====================

/**
 * Split Toposolid: click two points defining the split line; the picked (or
 * only) toposolid is divided into two independent solids along that line.
 */
export class SplitToposolidTool extends BaseTool {
    id = 'split_toposolid';
    name = 'Split Toposolid';
    cursor = 'crosshair';

    private pick: { element: Element; point: THREE.Vector3 } | null = null;
    private lineStart: THREE.Vector3 | null = null;
    private lineEnd: THREE.Vector3 | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.splitToposolidClickAToposolidThen'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (!this.pick) {
            const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Toposolid');
            if (!hit) {
                this.notify(i18n.t('tools.notify.clickAToposolidToSplit'), 'warning');
                return;
            }
            this.pick = hit;
            this.notify(i18n.t('tools.notify.nowClickTheFirstPointOf'), 'info');
            return;
        }
        if (!this.lineStart) {
            const point = this.getIntersection(event);
            if (point) this.lineStart = this.snapToGrid(point);
            return;
        }
        const point = this.getIntersection(event);
        if (!point) return;
        this.lineEnd = this.snapToGrid(point);
        this.doSplit();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || !this.lineStart) return;
        const point = this.getIntersection(event);
        if (point) {
            pushPolylinePreview(this.context, [this.lineStart], this.snapToGrid(point), '#c0392b');
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.reset();
        } else {
            super.onKeyDown(event);
        }
    }

    private reset(): void {
        this.pick = null;
        this.lineStart = null;
        this.lineEnd = null;
        this.context?.appStore.setToolPreview(null);
    }

    private doSplit(): void {
        if (!this.context || !this.pick || !this.lineStart || !this.lineEnd) return;
        const geom = (this.pick.element.geometryData as any) ?? {};
        const boundary: Polygon2D | undefined = geom.boundary;
        if (!boundary || boundary.length < 3) {
            this.notify(i18n.t('tools.notify.thePickedToposolidHasNoUsable'), 'error');
            this.reset();
            return;
        }
        const a: [number, number] = [this.lineStart.x, this.lineStart.y];
        const b: [number, number] = [this.lineEnd.x, this.lineEnd.y];
        const [left, right] = splitPolygonByLine(boundary, a, b);
        if (left.length < 3 || right.length < 3) {
            this.notify(i18n.t('tools.notify.theSplitLineDoesNotCross'), 'warning');
            this.reset();
            return;
        }
        const src = this.pick.element;
        const make = (poly: Polygon2D, suffix: string): Element => ({
            id: uuidv4(),
            category: 'Toposolid',
            type: src.type,
            name: `${src.name ?? src.type} (${suffix})`,
            parameters: {
                ...src.parameters,
                'Area': `${polygonArea(poly).toFixed(2)} m²`,
            },
            constraints: [...src.constraints],
            geometryData: { ...geom, boundary: poly },
        });
        const bim = this.context.bimStore;
        bim.removeElement(src.id);
        bim.addElement(make(left, 'A'));
        bim.addElement(make(right, 'B'));
        this.notify(i18n.t('tools.notify.toposolidSplitIntoTwoRegions'), 'success');
        this.reset();
    }
}

// ==================== Merge Surfaces ====================

/**
 * Merge Surfaces: pick two toposolids — they are fused into a single solid
 * whose boundary is the convex hull of both (the planar merge model).
 */
export class MergeSurfacesTool extends BaseTool {
    id = 'merge_surfaces';
    name = 'Merge Surfaces';
    cursor = 'crosshair';

    private picks: Element[] = [];

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.mergeSurfacesClickTwoToposolidsTo'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Toposolid');
        if (!hit) {
            this.notify(i18n.t('tools.notify.clickAToposolid'), 'warning');
            return;
        }
        if (!this.picks.some((p) => p.id === hit.element.id)) {
            this.picks.push(hit.element);
        }
        if (this.picks.length < 2) {
            this.notify(i18n.t('tools.notify.nowClickTheSecondToposolid'), 'info');
            return;
        }
        this.merge();
    }

    onPointerMove(): void {}
    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') this.picks = [];
        else super.onKeyDown(event);
    }

    private merge(): void {
        if (!this.context || this.picks.length < 2) return;
        const points: Polygon2D = [];
        this.picks.forEach((p) => {
            const b = (p.geometryData as any)?.boundary as Polygon2D | undefined;
            if (b) points.push(...b);
        });
        const hull = convexHull(points);
        if (hull.length < 3) {
            this.notify(i18n.t('tools.notify.thePickedSurfacesShareNoUsable'), 'error');
            this.picks = [];
            return;
        }
        const [first, ...rest] = this.picks;
        const geom = (first.geometryData as any) ?? {};
        const merged: Element = {
            id: uuidv4(),
            category: 'Toposolid',
            type: first.type,
            name: first.name ?? 'Merged Toposolid',
            parameters: { ...first.parameters, 'Area': `${polygonArea(hull).toFixed(2)} m²` },
            constraints: [...first.constraints],
            geometryData: { ...geom, boundary: hull },
        };
        const bim = this.context.bimStore;
        rest.forEach((r) => bim.removeElement(r.id));
        bim.removeElement(first.id);
        bim.addElement(merged);
        this.notify(i18n.t('tools.notify.surfacesMerged', { count: this.picks.length }), 'success');
        this.picks = [];
    }
}

// ==================== Subdivide Region ====================

/**
 * Subdivide: define a material sub-region inside a toposolid (road, lawn,
 * paving). Sketch the boundary; the created 'Site Subregions' element stores
 * the assigned material and is rendered as a tinted overlay on the terrain.
 */
export class SubdivideRegionTool extends BaseTool {
    id = 'subdivide_region';
    name = 'Subdivide';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;
    private host: Element | null = null;

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.subdivideClickAToposolidSketchThe'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (!this.host) {
            const hit = this.findElementHitAtPoint(event, (el) => el.category === 'Toposolid');
            if (!hit) {
                this.notify(i18n.t('tools.notify.clickAToposolidToSubdivide'), 'warning');
                return;
            }
            this.host = hit.element;
            this.notify(i18n.t('tools.notify.toposolidPickedSketchTheSubRegion'), 'info');
            return;
        }
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        if (this.points.length >= 3 && snapped.distanceTo(this.points[0]) < 0.3) {
            this.finish();
            return;
        }
        this.points.push(snapped);
        this.updatePreview();
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.points.length >= 3) {
            this.finish();
        } else if (event.key === 'Escape' && (this.points.length > 0 || this.host)) {
            this.points = [];
            this.currentPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    private updatePreview(): void {
        if (!this.context) return;
        pushPolylinePreview(this.context, this.points, this.currentPoint, '#4aa04a', true);
    }

    private finish(): void {
        if (!this.context || !this.host || this.points.length < 3) return;
        const boundary: Polygon2D = this.points.map((p) => [p.x, p.y]);
        const sub: Element = {
            id: uuidv4(),
            category: 'Site Subregions',
            type: 'Subregion: Paved',
            name: 'Subregion',
            parameters: {
                'Material': 'Paved',
                'Area': `${polygonArea(boundary).toFixed(2)} m²`,
                'Host Toposolid': this.host.id,
            },
            constraints: [{ type: 'HOST_CONTAINED', targetId: this.host.id, data: {} }],
            geometryData: { boundary },
        };
        this.context.bimStore.addElement(sub);
        this.notify(i18n.t('tools.notify.subRegionAssignedToTheToposolid'), 'success');
        this.points = [];
        this.currentPoint = null;
        this.host = null;
        this.context.appStore.setToolPreview(null);
    }
}

// ==================== Place Mass ====================

/**
 * Place Mass: point-placement of a parametric mass form (Box / Cylinder) at
 * the cursor — the library-placement sibling of In-Place Mass.
 */
export class PlaceMassTool extends BaseTool {
    id = 'place_mass';
    name = 'Place Mass';
    cursor = 'crosshair';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.placeMassClickToPlaceThe'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        const props = this.context.bimStore.toolProperties;
        const height = props.massHeight ?? 10;
        const formType = props.massFormType ?? 'Box';
        const footprint: Polygon2D = formType === 'Cylinder'
            ? regularPolygon(snapped.x, snapped.y, 5)
            : regularPolygon(snapped.x, snapped.y, 5, 4);

        const floors = buildMassFloors(polygonArea(footprint), height, props.massFloorSpacing ?? 4);
        const mass: MassElement = {
            id: uuidv4(),
            category: 'Mass',
            type: `Mass: ${formType}`,
            name: `Mass ${formType}`,
            parameters: {
                'Form': formType,
                'Height': `${height} m`,
                'Area': `${polygonArea(footprint).toFixed(2)} m²`,
                'Volume': `${(polygonArea(footprint) * height).toFixed(1)} m³`,
                'Floors': String(floors.length),
                'Family and Type': `Generic Mass: ${formType}`,
            },
            constraints: [],
            geometryData: { footprint, height, elevation: 0, floors, formType, isClosed: true },
        };
        this.context.bimStore.addElement(mass);
        this.notify(i18n.t('tools.notify.massPlaced', { formType: formType }), 'success');
        this.context.appStore.setActiveTool('select');
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            const props = this.context.bimStore.toolProperties;
            const formType = props.massFormType ?? 'Box';
            const footprint = formType === 'Cylinder'
                ? regularPolygon(snapped.x, snapped.y, 5)
                : regularPolygon(snapped.x, snapped.y, 5, 4);
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: footprint.map((p) => new THREE.Vector3(p[0], p[1], 0)),
                closed: true,
                color: '#880088',
            });
        }
    }

    onPointerUp(): void {}

    deactivate(): void {
        this.context?.appStore.setToolPreview(null);
        super.deactivate();
    }
}

/** Mass floor plates every `spacing` meters (BIM "Make From Geometry" floors). */
export function buildMassFloors(
    area: number,
    height: number,
    spacing: number
): { level: number; elevation: number; area: number }[] {
    if (spacing <= 0) return [];
    const floors: { level: number; elevation: number; area: number }[] = [];
    for (let e = spacing, level = 2; e < height; e += spacing, level++) {
        floors.push({ level, elevation: Number(e.toFixed(2)), area: Number(area.toFixed(2)) });
    }
    return floors;
}
